// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useRef, useState } from 'react';
import { AppIcon, TextInput } from '@loa/ui-components';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { fundsApi, type FundDTO } from '../../api/fundsApi';
import './FundSearchSelect.css';

export type FundSearchSelectProps = Readonly<{
  /** Called with the chosen catalogue fund. */
  onSelect: (fund: FundDTO) => void;
  /**
   * When set, a "custom fund" affordance is shown so the advisor can still
   * add a fund by name if funds-service is unavailable or the catalogue has
   * no match — never blocks the workflow behind the integration being on.
   */
  onManualSelect?: (fundName: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Disambiguates test/data hooks when several pickers share a screen. */
  testId?: string;
}>;

/**
 * Debounced funds-service fund search + pick. Types a query, calls
 * {@code GET /api/funds} (brokerage-filtered), and renders a results
 * dropdown; picking a row emits the {@link FundDTO} and clears the box.
 *
 * Resilient by design: if funds-service is disabled/unreachable the search
 * degrades to an "unavailable" note and (when {@link onManualSelect} is
 * given) still offers to add the typed text as a custom fund — so the
 * proposal fund pickers never hard-depend on the integration being wired.
 */
export function FundSearchSelect({
  onSelect,
  onManualSelect,
  placeholder = 'Search funds…',
  disabled = false,
  testId = 'fund-search-select',
}: FundSearchSelectProps) {
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(query.trim(), 300);
  const [results, setResults] = useState<FundDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [open, setOpen] = useState(false);
  const reqSeq = useRef(0);

  useEffect(() => {
    if (debounced.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    const seq = ++reqSeq.current;
    setLoading(true);
    setUnavailable(false);
    // NB: do NOT filter by onlyOpen here — a large share of catalogue funds
    // carry a null is_open (the aggregator doesn't always populate it), and
    // is_open IS TRUE would silently hide them from the picker. The advisor
    // should be able to find any catalogue fund.
    fundsApi
      .search({ q: debounced, size: 8 })
      .then((res) => {
        if (seq !== reqSeq.current) return;
        setResults(res.content);
        setLoading(false);
      })
      .catch(() => {
        if (seq !== reqSeq.current) return;
        setResults([]);
        setUnavailable(true);
        setLoading(false);
      });
  }, [debounced]);

  const pick = (fund: FundDTO) => {
    onSelect(fund);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const addManual = () => {
    const name = query.trim();
    if (!name || !onManualSelect) return;
    onManualSelect(name);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const showDropdown = open && query.trim().length >= 2;

  return (
    <div className="fund-search-select" data-testid={testId}>
      <TextInput
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        fluid
        iconLeft={<AppIcon name="search" size={16} />}
        aria-label={placeholder}
        data-testid={`${testId}-input`}
      />
      {showDropdown && (
        <div className="fund-search-select__menu" data-testid={`${testId}-menu`}>
          {loading && (
            <div className="fund-search-select__note" data-testid={`${testId}-loading`}>
              Searching…
            </div>
          )}
          {!loading &&
            results.map((fund) => (
              <button
                key={fund.fundKey}
                type="button"
                className="fund-search-select__option"
                onClick={() => pick(fund)}
                data-testid={`${testId}-option-${fund.fundKey}`}
              >
                <span className="fund-search-select__option-name">{fund.fundName}</span>
                {fund.providerDisplayName && (
                  <span className="fund-search-select__option-provider">
                    {fund.providerDisplayName}
                  </span>
                )}
              </button>
            ))}
          {!loading && results.length === 0 && (
            <div className="fund-search-select__note" data-testid={`${testId}-empty`}>
              {unavailable ? 'Fund search is unavailable.' : 'No matching funds.'}
            </div>
          )}
          {!loading && onManualSelect && (
            <button
              type="button"
              className="fund-search-select__option fund-search-select__option--manual"
              onClick={addManual}
              data-testid={`${testId}-add-manual`}
            >
              Add “{query.trim()}” as a custom fund
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default FundSearchSelect;
